import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Download, Type } from 'lucide-react';
import { FONT_SOURCES, fontsNeedTelling, reportNeedsTelling } from '../constants/indesign';
import './InDesignFontsDialog.css';

function keepFocusInside(event, card) {
  const stops = card.querySelectorAll('a[href], button');
  const first = stops[0];
  const last = stops[stops.length - 1];
  const at = window.document.activeElement;
  if (!card.contains(at) || (event.shiftKey ? at === first : at === last)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  }
}

// After an InDesign download: the fonts the file is set in and where each comes from (no font goes with the file),
// and InDesign's own failures on its text. Shown only when something is beyond Microsoft Office's fonts.
function InDesignFontsDialog({ report, onClose }) {
  const { t } = useTranslation('chat');
  const card = useRef(null);
  const shown = !!report && reportNeedsTelling(report);

  useEffect(() => {
    if (!shown) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Tab') {
        keepFocusInside(event, card.current);
      }
    };
    window.document.addEventListener('keydown', onKey);
    return () => window.document.removeEventListener('keydown', onKey);
  }, [shown, onClose]);

  if (!shown) return null;
  const tellsFonts = fontsNeedTelling(report);

  return createPortal(
    <div className="indesign-fonts-overlay" role="dialog" aria-modal="true" aria-labelledby="indesign-fonts-title"
      data-testid="indesign-fonts"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="indesign-fonts-card" ref={card}>
        <h2 id="indesign-fonts-title" className="indesign-fonts-title">
          <Type size={20} aria-hidden="true" />
          {t(tellsFonts ? 'indesignFonts.title' : 'indesignFonts.notesTitle')}
        </h2>
        {tellsFonts && (
          <>
            <p className="indesign-fonts-intro">{t('indesignFonts.intro')}</p>
            <ul className="indesign-fonts-list">
              {report.fonts.map((font) => (
                <li key={font.family} className="indesign-fonts-row" data-testid="indesign-font">
                  <span className="indesign-fonts-name">
                    <span className="indesign-fonts-family">{font.family}</span>
                    <span className="indesign-fonts-styles">{font.styles.join(', ')}</span>
                  </span>
                  {font.source === FONT_SOURCES.free ? (
                    <a className="indesign-fonts-get" href={font.url} target="_blank" rel="noopener noreferrer">
                      <Download size={14} aria-hidden="true" />
                      {t('indesignFonts.download')}
                    </a>
                  ) : (
                    <span className="indesign-fonts-where">{t(`indesignFonts.source.${font.source}`)}</span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
        {report.notes.map((note) => (
          <p key={note} className="indesign-fonts-note" data-testid="indesign-note">{t(`indesignFonts.note.${note}`)}</p>
        ))}
        <div className="indesign-fonts-actions">
          <button type="button" className="indesign-fonts-close" onClick={onClose} autoFocus>
            {t('indesignFonts.close')}
          </button>
        </div>
      </div>
    </div>,
    window.document.body,
  );
}

export default InDesignFontsDialog;
